/**
 * Batch Export Utilities
 *
 * Combines multiple artefacts into single documents (PowerPoint, Word, PDF)
 * Leverages existing export utilities for individual formats
 * Supports HTML artefacts as high-quality embedded images
 */

import type { Brand } from "@webuild-ai/react-auth";

import PptxGenJS from "pptxgenjs";
import { Document, Packer, Paragraph, HeadingLevel, ImageRun } from "docx";

import { Artefact, ArtefactType } from "@/types/artefacts";
import { logger } from "@/telemetry";
import { convertMarkdownToDocx, convertMarkdownToDocxParagraphs } from "@/utils/markdownToDocx";
import { renderMarkdownToPdf } from "@/utils/pdfRenderer";
import { markdownToHtml } from "@/utils/markdownConverter";
import { addMarkdownSlidesToPresentation } from "@/utils/markdownToPptx";
import {
  captureHtmlArtefactsAsImages,
  createImageMap,
  type CapturedImage,
  CAPTURE_WIDTH_PDF,
  CAPTURE_WIDTH_POWERPOINT,
  PAGE_CAPTURE_WIDTHS,
} from "@/utils/artefactExport/htmlToImage";
import { batchExportToExcel } from "@/utils/artefactExport/excelExport";
import { contentStartsWithTitle } from "@/utils/artefactExport/titleUtils";
import {
  resolveTemplate,
  applyWordTemplate,
  prepareTemplateData,
  applyPptxTemplate,
  preparePptxTemplateData,
  applyExcelTemplate,
  prepareExcelTemplateData,
} from "@/utils/templateEngine";
import {
  PDF_BATCH_TITLE_STYLE,
  PDF_BATCH_HEADING_STYLE,
  PDF_BATCH_CODE_STYLE,
  PDF_BATCH_CONTENT_STYLE,
  PDF_PAGEBREAK_CONFIG_WITH_IMAGES,
} from "@/utils/pdfStyles";

// PowerPoint slide configuration constants (in inches)
const PPTX_SLIDE_WIDTH = 10; // Standard PowerPoint slide width
const PPTX_SLIDE_HEIGHT = 7.5; // Standard PowerPoint slide height
const PPTX_MARGIN_HORIZONTAL = 0.5; // Left/right margin
const PPTX_MARGIN_TOP = 0.5; // Top margin
const PPTX_TITLE_HEIGHT = 0.75; // Title box height
const PPTX_CONTENT_TOP = 1.5; // Content starts at 1.5" (PPTX_MARGIN_TOP + PPTX_TITLE_HEIGHT + 0.25 spacing)

// PowerPoint font sizes
const PPTX_TITLE_SLIDE_MAIN_SIZE = 44; // Main title font size
const PPTX_TITLE_SLIDE_SUB_SIZE = 20; // Subtitle font size
const PPTX_SLIDE_TITLE_SIZE = 28; // Slide title font size
const PPTX_CODE_FONT_SIZE = 12; // Code content font size

// PowerPoint positioning
const PPTX_TITLE_SLIDE_Y_MAIN = 1.5; // Main title Y position
const PPTX_TITLE_SLIDE_Y_SUB = 2.5; // Subtitle Y position

// PowerPoint colour palette (hex without #)
const PPTX_COLOUR_TITLE = "28326E"; // Navy — slide titles and headings
const PPTX_COLOUR_SUBTITLE = "666666"; // Grey — subtitle text
const PPTX_COLOUR_BODY = "1a1a1a"; // Near-black — code / body text

// Word document configuration constants (in pixels)
// A4 content area: 160mm wide (~604px at 96 DPI)
// These values should match the capture widths for proper scaling
const WORD_IMAGE_MAX_WIDTH = 580; // Slightly smaller than capture width to ensure fit with margins
const WORD_IMAGE_MAX_HEIGHT = 800; // Max height to prevent overly tall images
const WORD_HEADING_SPACING_BEFORE = 400; // Spacing before headings
const WORD_HEADING_SPACING_AFTER = 200; // Spacing after headings

// PDF configuration constants
const PDF_MARGIN_MM = 10; // PDF page margin in millimeters
const PDF_IMAGE_QUALITY = 0.98; // JPEG quality for images in PDF
const PDF_HTML2CANVAS_SCALE = 2; // Scale for html2canvas rendering
const PDF_IMAGE_MARGIN_PX = 20; // Image margin in pixels

export type BatchExportFormat = "powerpoint" | "word" | "pdf" | "excel";

export interface BatchExportOptions {
  format: BatchExportFormat;
  title?: string;
  author?: string;
  includeTableOfContents?: boolean;
  useSeparateWorksheets?: boolean; // Excel-specific option
  brand?: Brand; // For Excel styling
  brandName?: string; // Brand name for template resolution
  theme?: "light" | "dark"; // For Excel styling
  fontFamily?: string; // Font for Word export
  codeFontFamily?: string; // Code font for Word export
  pageSize?: "A4" | "Letter" | "Legal"; // Page size for Word export (default: A4)
}

/**
 * Generate filename with timestamp for batch exports
 */
/* istanbul ignore next -- only called from ignored export functions */
function generateBatchFilename(format: BatchExportFormat): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T");
  const dateStr = timestamp[0];
  const timeStr = timestamp[1].substring(0, 8);
  const extensions = {
    powerpoint: "pptx",
    word: "docx",
    pdf: "pdf",
    excel: "xlsx",
  };

  return `batch_export_${dateStr}_${timeStr}.${extensions[format]}`;
}

/**
 * Combine multiple artefacts into single markdown document
 * Handles different artefact types appropriately
 * Note: HTML artefacts are skipped as they will be embedded as images
 */
export function combineArtefactsToMarkdown(
  artefacts: Artefact[],
  options: { includeTableOfContents?: boolean } = {},
): string {
  const sections: string[] = [];

  // Filter out HTML artefacts as they will be handled separately as images
  const markdownArtefacts = artefacts.filter((a) => a.type !== ArtefactType.HTML);

  // Optional table of contents
  if (options.includeTableOfContents && artefacts.length > 1) {
    sections.push("# Table of Contents\n");
    artefacts.forEach((artefact, index) => {
      sections.push(
        `${index + 1}. [${artefact.title}](#${artefact.title.toLowerCase().replace(/\s+/g, "-")})`,
      );
    });
    sections.push("\n---\n");
  }

  // Combine all artefacts
  markdownArtefacts.forEach((artefact, index) => {
    // Skip adding artefact title as heading - the content itself typically contains
    // the title already, and adding it creates duplicate headings in exports.
    // The document title is added separately by the export functions.

    // Add type-specific content formatting
    switch (artefact.type) {
      case ArtefactType.CODE:
        // Wrap code in code blocks with language
        sections.push(`\`\`\`${artefact.language || ""}\n${artefact.content}\n\`\`\`\n`);
        break;

      case ArtefactType.TABLE:
        // Tables are already in markdown format
        sections.push(artefact.content + "\n");
        break;

      case ArtefactType.DOCUMENT:
        // Documents are already markdown
        sections.push(artefact.content + "\n");
        break;

      default:
        sections.push(artefact.content + "\n");
    }

    // Add page/slide break between artefacts (except for last)
    if (index < markdownArtefacts.length - 1) {
      sections.push("\n---\n\n");
    }
  });

  return sections.join("\n");
}

/**
 * Export multiple artefacts to PowerPoint presentation
 * HTML artefacts are captured as images and embedded
 *
 * Note: This function is excluded from coverage because it requires complex
 * mocking of PptxGenJS library internals (slide creation, text/image positioning,
 * file generation). The function is tested through integration testing in the UI.
 */
/* istanbul ignore next */
export async function batchExportToPowerPoint(
  artefacts: Artefact[],
  options: Pick<
    BatchExportOptions,
    "title" | "author" | "fontFamily" | "codeFontFamily" | "brandName"
  > = {},
): Promise<void> {
  if (artefacts.length === 0) {
    throw new Error("No artefacts to export");
  }

  // Identify HTML artefacts that need image capture (charts/visualisations)
  const htmlArtefacts = artefacts.filter((a) => a.type === ArtefactType.HTML);

  // Tables are rendered as native OOXML tables in both the template path and the
  // programmatic (PptxGenJS) path, so only HTML artefacts need image capture.
  let imageMap = new Map<string, CapturedImage>();

  if (htmlArtefacts.length > 0) {
    try {
      const capturedImages = await captureHtmlArtefactsAsImages(htmlArtefacts, {
        width: CAPTURE_WIDTH_POWERPOINT,
      });

      imageMap = createImageMap(capturedImages);
    } catch (error) {
      logger.error("Failed to capture HTML artefacts as images:", error);
      throw new Error(
        `Failed to capture charts: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  // Template resolution: if a PPTX template is available, use it instead of programmatic export
  if (options.brandName) {
    try {
      const template = await resolveTemplate("pptx", options.brandName);

      if (template) {
        // Convert CapturedImage map to PptxImageData map for the template engine
        const pptxImageMap = new Map(
          [...imageMap.entries()].map(([id, img]) => [
            id,
            { imageData: img.imageData, width: img.width, height: img.height },
          ]),
        );

        const data = preparePptxTemplateData(artefacts, {
          title: options.title,
          author: options.author,
          brand: options.brandName,
          imageMap: pptxImageMap.size > 0 ? pptxImageMap : undefined,
        });

        const blob = await applyPptxTemplate(template.data, data);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const filename = generateBatchFilename("powerpoint");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return;
      }
    } catch (error) {
      logger.warn("Template export failed, falling back to programmatic export", {
        error: String(error),
      });
    }
  }

  // Create presentation and process artefacts in original order
  const finalPptx = new PptxGenJS();

  // Extract font options with defaults
  const fontFamily = options.fontFamily; // undefined = PowerPoint default
  const codeFontFamily = options.codeFontFamily || "Courier New";

  // Title slide
  const titleSlide = finalPptx.addSlide();

  titleSlide.addText(options.title || "Generated Content Export", {
    x: PPTX_MARGIN_HORIZONTAL,
    y: PPTX_TITLE_SLIDE_Y_MAIN,
    w: "90%",
    h: 1,
    fontSize: PPTX_TITLE_SLIDE_MAIN_SIZE,
    bold: true,
    color: PPTX_COLOUR_TITLE,
    ...(fontFamily && { fontFace: fontFamily }),
  });
  titleSlide.addText(`${artefacts.length} items`, {
    x: PPTX_MARGIN_HORIZONTAL,
    y: PPTX_TITLE_SLIDE_Y_SUB,
    w: "90%",
    fontSize: PPTX_TITLE_SLIDE_SUB_SIZE,
    color: PPTX_COLOUR_SUBTITLE,
    ...(fontFamily && { fontFace: fontFamily }),
  });

  // Process each artefact in order
  for (const artefact of artefacts) {
    if (artefact.type === ArtefactType.HTML) {
      // HTML artefact - add as image slide
      const image = imageMap.get(artefact.id);

      if (image) {
        const slide = finalPptx.addSlide();

        slide.addText(artefact.title, {
          x: PPTX_MARGIN_HORIZONTAL,
          y: PPTX_MARGIN_TOP,
          w: "90%",
          h: PPTX_TITLE_HEIGHT,
          fontSize: PPTX_SLIDE_TITLE_SIZE,
          bold: true,
          color: PPTX_COLOUR_TITLE,
          ...(fontFamily && { fontFace: fontFamily }),
        });

        // Calculate optimal dimensions for slide (standard slide is 10" x 7.5")
        const aspectRatio = image.height / image.width;
        const maxWidth = PPTX_SLIDE_WIDTH - 2 * PPTX_MARGIN_HORIZONTAL; // Leave margins on each side
        const maxHeight = PPTX_SLIDE_HEIGHT - PPTX_CONTENT_TOP - PPTX_MARGIN_HORIZONTAL; // Leave room for title and bottom margin

        let imageWidth: number;
        let imageHeight: number;

        if (aspectRatio > maxHeight / maxWidth) {
          // Tall image: constrain by height
          imageHeight = maxHeight;
          imageWidth = imageHeight / aspectRatio;
        } else {
          // Wide image: constrain by width
          imageWidth = maxWidth;
          imageHeight = imageWidth * aspectRatio;
        }

        slide.addImage({
          data: image.base64,
          x: (PPTX_SLIDE_WIDTH - imageWidth) / 2, // Center horizontally
          y: PPTX_CONTENT_TOP, // Fixed position below title
          w: imageWidth,
          h: imageHeight,
        });
      }
    } else if (artefact.type === ArtefactType.CODE) {
      // CODE artefact - single slide with monospace font
      const slide = finalPptx.addSlide();

      // Add title
      slide.addText(artefact.title, {
        x: PPTX_MARGIN_HORIZONTAL,
        y: PPTX_MARGIN_TOP,
        w: "90%",
        h: PPTX_TITLE_HEIGHT,
        fontSize: PPTX_SLIDE_TITLE_SIZE,
        bold: true,
        color: PPTX_COLOUR_TITLE,
        ...(fontFamily && { fontFace: fontFamily }),
      });

      // Code block with monospace font - keep as-is for literal display
      slide.addText(artefact.content, {
        x: PPTX_MARGIN_HORIZONTAL,
        y: PPTX_CONTENT_TOP,
        w: "90%",
        h: "80%",
        fontSize: PPTX_CODE_FONT_SIZE,
        fontFace: codeFontFamily,
        valign: "top",
        color: PPTX_COLOUR_BODY,
      });
    } else {
      // DOCUMENT/TABLE artefacts - use multi-slide markdown conversion
      // This properly spreads content across multiple slides when needed
      await addMarkdownSlidesToPresentation(finalPptx, artefact.content, {
        title: artefact.title,
        fontFamily,
        codeFontFamily,
      });
    }
  }

  const filename = generateBatchFilename("powerpoint");

  await finalPptx.writeFile({ fileName: filename });
}

/**
 * Export multiple artefacts to Word document
 * HTML artefacts are captured as images and embedded
 *
 * Note: This function is excluded from coverage because it requires complex
 * mocking of docx library internals (Document creation, paragraph/image insertion,
 * Blob generation). The function is tested through integration testing in the UI.
 */
/**
 * Wrap a pre-built Word content blob with a branded template cover page,
 * if a template is available for the current brand. Otherwise returns the
 * content blob unchanged.
 */
/* istanbul ignore next -- only called from ignored export functions */
async function wrapWithTemplateIfAvailable(
  contentBlob: Blob,
  artefacts: Artefact[],
  options: { title?: string; author?: string; brandName?: string },
): Promise<Blob> {
  if (!options.brandName) return contentBlob;

  try {
    const template = await resolveTemplate("word", options.brandName);

    if (template) {
      const data = prepareTemplateData(artefacts, {
        title: options.title,
        author: options.author,
        brand: options.brandName,
      });

      return applyWordTemplate(template.data, data, contentBlob);
    }
  } catch (error) {
    logger.warn("Template wrapping failed, returning content without template", {
      error: String(error),
    });
  }

  return contentBlob;
}

/* istanbul ignore next */
export async function batchExportToWord(
  artefacts: Artefact[],
  options: Pick<
    BatchExportOptions,
    | "title"
    | "author"
    | "includeTableOfContents"
    | "fontFamily"
    | "codeFontFamily"
    | "pageSize"
    | "brandName"
  > = {},
): Promise<Blob> {
  if (artefacts.length === 0) {
    throw new Error("No artefacts to export");
  }

  // Separate HTML and non-HTML artefacts
  const htmlArtefacts = artefacts.filter((a) => a.type === ArtefactType.HTML);
  const nonHtmlArtefacts = artefacts.filter((a) => a.type !== ArtefactType.HTML);

  // Capture HTML artefacts as images with page-size aware width for proper document fit
  // Use the page size option to determine optimal capture width
  const captureWidth = PAGE_CAPTURE_WIDTHS[options.pageSize || "A4"];
  let imageMap = new Map<string, CapturedImage>();

  if (htmlArtefacts.length > 0) {
    try {
      const capturedImages = await captureHtmlArtefactsAsImages(htmlArtefacts, {
        width: captureWidth,
      });

      imageMap = createImageMap(capturedImages);
    } catch (error) {
      logger.error("Failed to capture HTML artefacts as images:", error);
      throw new Error(
        `Failed to capture HTML charts: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  // Build document sections
  const sections: Array<Paragraph | any> = [];

  // Title - Use HEADING_1 for proper H1 formatting (TITLE is metadata-style, not a visible heading)
  sections.push(
    new Paragraph({
      text: options.title || "Generated Content Export",
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 0, after: WORD_HEADING_SPACING_AFTER },
    }),
  );

  // Process each artefact in order
  for (const artefact of artefacts) {
    // Only add artefact title if content doesn't already start with it
    // This avoids duplicate titles in the exported document
    if (!contentStartsWithTitle(artefact.content, artefact.title)) {
      sections.push(
        new Paragraph({
          text: artefact.title,
          heading: HeadingLevel.HEADING_1,
          spacing: { before: WORD_HEADING_SPACING_BEFORE, after: WORD_HEADING_SPACING_AFTER },
        }),
      );
    }

    if (artefact.type === ArtefactType.HTML) {
      // HTML artefact - embed as image
      const image = imageMap.get(artefact.id);

      if (image) {
        // Calculate display dimensions with max width and height constraints
        // Images captured at 2x scale have excellent quality
        const maxWidth = WORD_IMAGE_MAX_WIDTH; // Increased for larger chart display
        const maxHeight = WORD_IMAGE_MAX_HEIGHT; // Increased to maintain better visibility

        // Calculate aspect ratio
        const aspectRatio = image.width / image.height;

        // Determine final dimensions based on aspect ratio
        let displayWidth: number;
        let displayHeight: number;

        if (aspectRatio > maxWidth / maxHeight) {
          // Wide image: constrain by width
          displayWidth = maxWidth;
          displayHeight = Math.round(maxWidth / aspectRatio);
        } else {
          // Tall image: constrain by height
          displayHeight = maxHeight;
          displayWidth = Math.round(maxHeight * aspectRatio);
        }

        sections.push(
          new Paragraph({
            children: [
              new ImageRun({
                data: image.imageData,
                transformation: {
                  width: displayWidth,
                  height: displayHeight,
                },
                type: "png",
              }),
            ],
            spacing: {
              after: 0, // No spacing after image
            },
          }),
        );
      }
    } else {
      // Non-HTML artefact - use markdown conversion for proper formatting
      // Pass font context if provided
      const fontContext = options.fontFamily
        ? {
            fontFamily: options.fontFamily,
            codeFontFamily: options.codeFontFamily || "Courier New",
          }
        : undefined;
      const { paragraphs } = await convertMarkdownToDocxParagraphs(artefact.content, fontContext);

      sections.push(...paragraphs);
    }
  }

  // If we have non-HTML artefacts, use the existing markdown converter for better formatting
  if (nonHtmlArtefacts.length > 0 && htmlArtefacts.length === 0) {
    // Pure markdown conversion path (better formatting)
    const combinedMarkdown = combineArtefactsToMarkdown(artefacts, {
      includeTableOfContents: options.includeTableOfContents,
    });

    const fullMarkdown = `# ${options.title || "Generated Content Export"}\n\n${combinedMarkdown}`;

    const contentBlob = await convertMarkdownToDocx(fullMarkdown, {
      title: options.title || "Generated Content Export",
      creator: options.author || "",
      description: `Export of ${artefacts.length} items`,
      fontFamily: options.fontFamily,
      codeFontFamily: options.codeFontFamily,
      pageSize: options.pageSize,
    });

    return wrapWithTemplateIfAvailable(contentBlob, artefacts, options);
  }

  // Page size dimensions in twips (default to A4)
  const pageSizes = {
    A4: { width: 11906, height: 16838 },
    Letter: { width: 12240, height: 15840 },
    Legal: { width: 12240, height: 20160 },
  };
  const pageSize = pageSizes[options.pageSize || "A4"];

  // Mixed content or pure HTML - use manual construction
  const doc = new Document({
    creator: options.author || "",
    title: options.title || "Generated Content Export",
    description: `Export of ${artefacts.length} items`,
    sections: [
      {
        properties: {
          page: {
            size: {
              width: pageSize.width,
              height: pageSize.height,
            },
          },
        },
        children: sections,
      },
    ],
  });

  const contentBlob = await Packer.toBlob(doc);

  return wrapWithTemplateIfAvailable(contentBlob, artefacts, options);
}

/**
 * Export multiple artefacts to PDF document
 * HTML artefacts are captured as images and embedded
 *
 * Note: This function is excluded from coverage because it requires complex
 * mocking of html2pdf.js library and markdown conversion pipelines.
 * The function is tested through integration testing in the UI.
 */
/* istanbul ignore next */
export async function batchExportToPDF(
  artefacts: Artefact[],
  options: Pick<BatchExportOptions, "title" | "author" | "includeTableOfContents" | "brand"> = {},
): Promise<any> {
  if (artefacts.length === 0) {
    throw new Error("No artefacts to export");
  }

  // For PDF, we'll use the markdown converter for non-HTML artefacts
  // and add a note about HTML content limitations
  const htmlArtefacts = artefacts.filter((a) => a.type === ArtefactType.HTML);

  if (htmlArtefacts.length === 0) {
    // No HTML artefacts - use standard markdown conversion
    const combinedMarkdown = combineArtefactsToMarkdown(artefacts, {
      includeTableOfContents: options.includeTableOfContents,
    });

    const fullMarkdown = `# ${options.title || "Generated Content Export"}\n\n${combinedMarkdown}`;

    return renderMarkdownToPdf(fullMarkdown, {
      title: options.title || "Generated Content Export",
      author: options.author || "",
      brandFontFamily: options.brand?.fontFamily,
      brandCodeFontFamily: options.brand?.codeFontFamily,
    });
  }

  // Capture HTML artefacts as images with PDF-optimised width
  const capturedImages = await captureHtmlArtefactsAsImages(htmlArtefacts, {
    width: CAPTURE_WIDTH_PDF,
  });
  const imageMap = createImageMap(capturedImages);

  // For PDF with HTML content, we need to use a different approach
  // Using html2pdf.js for better image embedding support
  const html2pdf = (await import("html2pdf.js")).default;

  // Build HTML content for PDF generation - minimal structure to avoid blank pages
  const title = options.title || "Generated Content Export";

  // Build content sections
  const sections: string[] = [];

  for (const artefact of artefacts) {
    let sectionHtml = "";

    // Only add artefact title if content doesn't already start with it
    // This avoids duplicate titles in the exported document
    if (!contentStartsWithTitle(artefact.content, artefact.title)) {
      sectionHtml += `<h2 style="${PDF_BATCH_HEADING_STYLE}">${artefact.title}</h2>`;
    }

    if (artefact.type === ArtefactType.HTML) {
      const image = imageMap.get(artefact.id);

      if (image) {
        sectionHtml += `<img src="${image.base64}" style="max-width: 100%; height: auto; margin: ${PDF_IMAGE_MARGIN_PX}px 0; display: block;" />`;
      }
    } else {
      const convertedHtml = markdownToHtml(artefact.content);

      sectionHtml +=
        artefact.type === ArtefactType.CODE
          ? `<pre style="${PDF_BATCH_CODE_STYLE}"><code>${artefact.content}</code></pre>`
          : `<div style="${PDF_BATCH_CONTENT_STYLE}">${convertedHtml}</div>`;
    }

    sections.push(sectionHtml);
  }

  // Build a complete HTML document structure (like renderMarkdownToPdf does)
  // This is required for html2pdf.js to render properly
  const htmlContent = `
    <html>
      <head>
        <meta charset="UTF-8">
        <style>
          /* Prevent page breaks inside elements to avoid canvas slicing artifacts */
          p, li, h1, h2, h3, h4, h5, h6, tr, blockquote, pre, code, ul, ol {
            page-break-inside: avoid;
          }

          /* Keep headings with their following content */
          h1, h2, h3, h4, h5, h6 {
            page-break-after: avoid;
          }

          /* Prevent orphaned headings and content */
          p {
            orphans: 3;
            widows: 3;
          }

          /* Code blocks keep their grey background */
          pre, code {
            background-color: #f5f5f5;
          }

          /* HR separators */
          hr {
            margin: 24px 0;
            padding: 0;
            border-top: 1px solid #ddd;
          }

          /* Base body styles */
          body {
            font-family: Arial, sans-serif;
            color: #000;
            margin: 0;
            padding: 0;
          }

          /* Image styles */
          img {
            max-width: 100%;
            height: auto;
          }
        </style>
      </head>
      <body>
        <h1 style="${PDF_BATCH_TITLE_STYLE}">${title}</h1>
        ${sections.join('<hr style="margin: 24px 0; border: none; border-top: 1px solid #ddd;">')}
      </body>
    </html>
  `;

  // Pass HTML string directly to html2pdf (like renderMarkdownToPdf does)
  // This avoids issues with off-screen DOM elements and html2canvas
  const opt = {
    margin: PDF_MARGIN_MM,
    filename: generateBatchFilename("pdf"),
    image: { type: "jpeg" as const, quality: PDF_IMAGE_QUALITY },
    html2canvas: {
      scale: PDF_HTML2CANVAS_SCALE,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
    },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" as const },
    // Use aggressive pagebreak config to prevent content clipping with black bars
    pagebreak: PDF_PAGEBREAK_CONFIG_WITH_IMAGES,
  };

  const pdfDoc = await html2pdf().set(opt).from(htmlContent).toPdf().get("pdf");

  // Set PDF document metadata for proper title/author in document properties
  if (options.title) {
    pdfDoc.setProperties({ title: options.title });
  }
  if (options.author) {
    pdfDoc.setProperties({ author: options.author });
  }

  return pdfDoc;
}

/**
 * Export multiple artefacts to PDF and return as ArrayBuffer for preview
 * This is a wrapper around batchExportToPDF that returns data instead of auto-downloading
 */
/* istanbul ignore next */
export async function batchExportToPDFBlob(
  artefacts: Artefact[],
  options: Pick<BatchExportOptions, "title" | "author" | "includeTableOfContents"> = {},
): Promise<ArrayBuffer> {
  const pdfDoc = await batchExportToPDF(artefacts, options);

  return pdfDoc.output("arraybuffer");
}

/**
 * Export multiple artefacts to Word and return as ArrayBuffer for preview
 * This is a wrapper around batchExportToWord that returns ArrayBuffer instead of Blob
 */
/* istanbul ignore next */
export async function batchExportToWordBlob(
  artefacts: Artefact[],
  options: Pick<
    BatchExportOptions,
    "title" | "author" | "includeTableOfContents" | "fontFamily" | "codeFontFamily" | "brandName"
  > = {},
): Promise<ArrayBuffer> {
  const blob = await batchExportToWord(artefacts, options);

  return blob.arrayBuffer();
}

/**
 * Unified batch export function
 *
 * Note: This function is excluded from coverage because it delegates to library-specific
 * export functions (PowerPoint, Word, PDF) which are integration-tested in the UI.
 * Only the Excel branch can be unit tested due to ExcelJS mock compatibility.
 */
/* istanbul ignore next */
export async function batchExport(
  artefacts: Artefact[],
  options: BatchExportOptions,
): Promise<void> {
  switch (options.format) {
    case "powerpoint":
      await batchExportToPowerPoint(artefacts, {
        title: options.title,
        author: options.author,
        fontFamily: options.fontFamily,
        codeFontFamily: options.codeFontFamily,
        brandName: options.brandName,
      });
      break;

    case "word": {
      const blob = await batchExportToWord(artefacts, options);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const filename = generateBatchFilename("word");

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      break;
    }

    case "pdf": {
      const doc = await batchExportToPDF(artefacts, options);
      const filename = generateBatchFilename("pdf");

      doc.save(filename);
      break;
    }

    case "excel": {
      // Template resolution: if an Excel template is available, use it
      if (options.brandName) {
        try {
          const template = await resolveTemplate("xlsx", options.brandName);

          if (template) {
            const data = prepareExcelTemplateData(artefacts, {
              title: options.title,
              author: options.author,
              brand: options.brandName,
            });

            // Capture HTML artefacts as images for template embedding
            const templateHtmlArtefacts = artefacts.filter((a) => a.type === ArtefactType.HTML);
            let templateImageMap: Map<string, CapturedImage> | undefined;

            if (templateHtmlArtefacts.length > 0) {
              try {
                const capturedImages = await captureHtmlArtefactsAsImages(templateHtmlArtefacts, {
                  width: PAGE_CAPTURE_WIDTHS.A4,
                });

                templateImageMap = createImageMap(capturedImages);
              } catch (error) {
                logger.warn("Failed to capture chart images for Excel template export:", {
                  error: String(error),
                });
              }
            }

            const blob = await applyExcelTemplate(template.data, data, templateImageMap, {
              useSeparateWorksheets: options.useSeparateWorksheets,
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            const filename = generateBatchFilename("excel");

            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            break;
          }
        } catch (error) {
          logger.warn("Excel template export failed, falling back to programmatic export", {
            error: String(error),
          });
        }
      }

      // Excel export requires brand and theme for styling
      if (!options.brand || !options.theme) {
        throw new Error("Brand and theme are required for Excel export");
      }

      // Capture HTML artefacts as images (for dimension info in placeholders)
      // Excel uses A4-based width as images are embedded similarly to documents
      const htmlArtefacts = artefacts.filter((a) => a.type === ArtefactType.HTML);
      let imageMap: Map<string, any> | undefined;

      if (htmlArtefacts.length > 0) {
        try {
          const capturedImages = await captureHtmlArtefactsAsImages(htmlArtefacts, {
            width: PAGE_CAPTURE_WIDTHS.A4,
          });

          imageMap = createImageMap(capturedImages);
        } catch (error) {
          logger.warn("Failed to capture chart images for Excel export:", { error: String(error) });
          // Continue without images - will show placeholder text
        }
      }

      await batchExportToExcel(
        artefacts,
        {
          useSeparateWorksheets: options.useSeparateWorksheets,
          fontFamily: options.fontFamily,
          codeFontFamily: options.codeFontFamily,
        },
        options.title || "Generated Content",
        options.brand,
        options.theme,
        imageMap,
      );
      break;
    }

    default:
      throw new Error(`Unsupported export format: ${options.format}`);
  }
}
